import type React from 'react';
import { useMemo, useState } from 'react';
import { Icon, IconButton, Button, Avatar, Badge, MemberRow, Input, Kbd } from '@yurt/ui';
import { fingerprint, dmChannel, agentDmChannel, guestDmChannel, type Msg } from '@yurt/protocol';
import { useApp } from '../store';
import { useSwipe } from './touch';
import { useCurrent, roster, personFor, authorKey, channelTitle, prefsLine, type Person } from '../model';
import { fmtTime, fmtDay } from '../lib/format';
import { MessageItem, type MsgCtx } from './Message';
import { Composer } from './Composer';
import { useTyping } from './ChannelView';
import { messageOf, must } from './must';
import { WorkPanel, FollowButton } from './Collab';
import { DocPanel } from './DocView';

export function RightPanel({ narrow }: { narrow: boolean }) {
  const panel = useApp((s) => s.panel);
  const { route, state } = useCurrent();
  const code = must(route.code, 'The side panel only opens inside a workspace');
  const close = () => {
    if (panel.type === 'thread') useApp.getState().go({ code, ch: route.ch });
    useApp.getState().setPanel({ type: null });
  };
  // Full screen on narrow screens: swipe it away to the right, like going back.
  const swipe = useSwipe({ right: close });
  const titles = { members: 'Members', profile: 'Profile', thread: 'Thread', pinned: 'Pinned', search: 'Search', work: 'Hub', doc: 'Doc' } as const;
  // Docs and boards need room to write and lay out notes.
  const wide = panel.type === 'doc';
  const board = panel.type === 'doc' && state?.docs.get(panel.id ?? '')?.kind === 'board';
  const title = board ? 'Board' : titles[must(panel.type, 'The side panel is mounted for an open panel')];
  return (
    <aside
      aria-label={title}
      {...(narrow ? swipe.handlers : {})}
      style={
        narrow
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 'var(--z-dialog)',
              background: 'var(--surface-page)',
              display: 'flex',
              flexDirection: 'column',
              padding: 'var(--safe-top) var(--safe-right) var(--safe-bottom) var(--safe-left)',
              touchAction: 'pan-y',
              transform: swipe.offset > 0 ? 'translateX(' + swipe.offset + 'px)' : undefined,
              animation: 'ag-rise var(--dur-base) var(--ease-out)',
            }
          : {
              width: wide ? 'min(520px, 40vw)' : 'var(--layout-panel, 320px)',
              minWidth: 300,
              flexShrink: 0,
              borderLeft: '1px solid var(--border-subtle)',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              background: 'var(--surface-page)',
            }
      }
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, height: 56, padding: '0 8px 0 18px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <span style={{ flex: 1, font: '700 16px/1 var(--font-display)', letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{title}</span>
        <IconButton icon="x" label="Close (Esc)" size="sm" onClick={close} />
      </header>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {panel.type === 'members' && <Members code={code} />}
        {panel.type === 'profile' && panel.id && <Profile id={panel.id} code={code} />}
        {panel.type === 'thread' && panel.id && <Thread id={panel.id} code={code} ch={must(route.ch, 'A thread route names its channel')} />}
        {panel.type === 'pinned' && <Pinned code={code} />}
        {panel.type === 'search' && <Search code={code} />}
        {panel.type === 'work' && state && <WorkPanel tab={panel.id} state={state} code={code} />}
        {panel.type === 'doc' && panel.id && state && <DocPanel id={panel.id} state={state} />}
      </div>
    </aside>
  );
}

/** The second line under a member: whose an agent is and what it's doing, or a person's role. */
function memberMeta(p: Person): string | undefined {
  if (p.kind === 'agent')
    return (p.owner?.self ? 'Yours' : p.owner?.name + '’s') + (p.presence === 'offline' ? ' · machine off' : p.workingOn ? ' · on ' + p.workingOn : p.working ? ' · working' : '');
  const role = p.creator ? 'Creator' : p.admin ? 'Admin' : undefined;
  return p.focus ? [role, 'Focusing'].filter(Boolean).join(' · ') : role;
}

/** Admins: people who asked to join through an invite link, to let in or turn away. */
function JoinRequests({ code }: { code: string }) {
  const { state, peer, identity } = useCurrent();
  const asking = state?.admins.has(identity.pub) ? (peer?.joinRequests ?? []) : [];
  if (!asking.length) return null;
  const app = useApp.getState();
  return (
    <div data-testid="join-requests" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 10px 0' }}>
      <div style={{ padding: '4px 0', fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-subtle)' }}>
        Asking to join · {asking.length}
      </div>
      {asking.map((r) => (
        <div key={r.pub} data-testid="join-request" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 120, fontSize: 13.5, color: 'var(--text-body)' }}>
            {r.name}
            <span style={{ marginLeft: 6, font: '400 12px var(--font-mono)', color: 'var(--text-subtle)' }}>{fingerprint(r.pub)}</span>
          </span>
          <Button size="sm" variant="primary" data-testid="join-admit" onClick={() => app.admit(code, r.pub, true)}>
            Let in
          </Button>
          <Button size="sm" variant="ghost" data-testid="join-decline" onClick={() => app.admit(code, r.pub, false)}>
            Turn away
          </Button>
        </div>
      ))}
    </div>
  );
}

function Members({ code }: { code: string }) {
  const { state, peer, identity } = useCurrent();
  const people = roster(state, peer, identity.pub);
  const open = (p: Person) => useApp.getState().setPanel({ type: 'profile', id: p.id });
  const group = (title: string, list: Person[]) =>
    list.length > 0 && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ padding: '8px 10px 4px', fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-subtle)' }}>
          {title} · {list.length}
        </div>
        {list.map((p) => (
          <MemberRow key={p.id} member={p} onClick={() => open(p)} cutout="var(--surface-page)" meta={memberMeta(p)} />
        ))}
      </div>
    );
  return (
    <div style={{ overflow: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <JoinRequests code={code} />
      {group(
        'People',
        people.filter((p) => p.kind === 'human'),
      )}
      {group(
        'Agents',
        people.filter((p) => p.kind === 'agent'),
      )}
      <div style={{ padding: '4px 10px' }}>
        <Button variant="secondary" size="sm" iconLeft="user-plus" onClick={() => useApp.getState().setDialog('invite')}>
          Invite people
        </Button>
      </div>
    </div>
  );
}

function Line({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 13.5, padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
      <span style={{ width: 96, color: 'var(--text-subtle)', flexShrink: 0 }}>{k}</span>
      <span style={{ color: 'var(--text-body)', fontFamily: mono ? 'var(--font-mono)' : undefined, fontSize: mono ? 12.5 : undefined, minWidth: 0, overflowWrap: 'anywhere' }}>
        {v}
      </span>
    </div>
  );
}

const presenceLabel = (p: Person) => (p.presence === 'online' ? 'Online' : p.presence === 'away' ? 'Away' : 'Offline');

function ProfileHeader({ p }: { p: Person }) {
  return (
    <>
      <Avatar
        name={p.name}
        kind={p.kind}
        self={p.self}
        owner={p.kind === 'agent' ? p.owner : undefined}
        presence={p.presence}
        working={p.working}
        size={72}
        decorative
        cutout="var(--surface-page)"
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ font: '700 24px/1.1 var(--font-display)', letterSpacing: '-0.035em', color: 'var(--text-strong)' }}>{p.name}</span>
          {p.kind === 'agent' && (
            <Badge tone="agent" size="sm" icon="sparkles">
              Agent
            </Badge>
          )}
          {p.creator && (
            <Badge tone="accent" size="sm" icon="crown">
              Creator
            </Badge>
          )}
          {!p.creator && p.admin && (
            <Badge tone="accent" size="sm" icon="shield-check">
              Admin
            </Badge>
          )}
          {p.banned && (
            <Badge tone="danger" size="sm" icon="ban">
              Banned
            </Badge>
          )}
        </div>
        <span style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>
          @{p.handle} · {presenceLabel(p)}
          {p.focus ? ' · in focus mode, may answer later' : ''}
          {p.workingOn ? ' · working on ' + p.workingOn : ''}
        </span>
      </div>
      <div>
        {p.kind === 'agent' ? (
          <>
            <Line k="Owner" v={p.owner?.self ? 'You' : p.owner?.name} />
            <Line k="Runtime" v={p.runtime || '—'} />
            <Line k="Runs on" v={(p.owner?.self ? 'Your' : p.owner?.name + '’s') + ' machine'} />
            <Line k="Owner key" v={fingerprint(p.pub)} mono />
            {p.prefs && <Line k="Settings" v={<span data-testid="agent-prefs">{prefsLine(p.prefs)}</span>} />}
          </>
        ) : (
          <Line k="Key" v={fingerprint(p.pub)} mono />
        )}
      </div>
    </>
  );
}

/** Message a person; message, configure or (if it's discoverable) DM an agent. */
function ProfileActions({ p, code, me }: { p: Person; code: string; me: string }) {
  const app = useApp.getState();
  if (p.kind === 'human')
    return (
      <>
        <Button variant="primary" size="sm" iconLeft="message-square" onClick={() => app.go({ code, ch: dmChannel(me, p.pub) })}>
          {p.self ? 'Notes to self' : 'Message'}
        </Button>
        {!p.self && p.presence === 'online' && <FollowButton pub={p.pub} />}
      </>
    );
  const agentId = must(p.agentId, 'an agent person carries its agent id');
  if (p.owner?.self)
    return (
      <>
        <Button variant="agent" size="sm" iconLeft="lock" onClick={() => app.go({ code, ch: agentDmChannel(me, agentId) })}>
          Message privately
        </Button>
        <Button variant="secondary" size="sm" iconLeft="external-link" onClick={() => window.open('http://127.0.0.1:7717/', '_blank')}>
          Configure
        </Button>
      </>
    );
  return p.prefs?.discoverable ? (
    <Button variant="agent" size="sm" iconLeft="message-square" data-testid="agent-message" onClick={() => app.go({ code, ch: guestDmChannel(me, p.pub, agentId) })}>
      Message
    </Button>
  ) : (
    <span data-testid="agent-not-discoverable" style={{ fontSize: 12.5, color: 'var(--text-subtle)' }}>
      Not discoverable: @mention {p.name} in a channel or reply to its messages.
    </span>
  );
}

/** Admin tools for a member: roles and bans. */
function Moderation({ p, code }: { p: Person; code: string }) {
  const { state, peer, identity } = useCurrent();
  const app = useApp.getState();
  // A ban rotates the key, which can't be undone, so it asks for a second click.
  const [confirmBan, setConfirmBan] = useState(false);
  const iAmCreator = state?.creator === identity.pub;
  const ban = () => {
    setConfirmBan(false);
    app.publish(code, { t: 'ban', b: { target: p.pub, on: true } });
    for (const pid of peer?.peerIdsFor([p.pub]) || []) (peer?.room?.getPeers()[pid] as RTCPeerConnection | undefined)?.close();
    try {
      peer?.rotate();
      app.toast({
        tone: 'success',
        title: p.name + ' was removed',
        description: 'The workspace key was rotated: they can’t read anything new.',
      });
    } catch (err) {
      app.toast({ tone: 'danger', title: 'Banned, but the key wasn’t rotated', description: messageOf(err), duration: 10_000 });
    }
  };
  const banButton = p.banned ? (
    <Button size="sm" variant="secondary" onClick={() => app.publish(code, { t: 'ban', b: { target: p.pub, on: false } })}>
      Unban
    </Button>
  ) : !confirmBan ? (
    <Button size="sm" variant="danger" iconLeft="ban" data-testid="ban-button" onClick={() => setConfirmBan(true)}>
      Ban
    </Button>
  ) : (
    <Button size="sm" variant="danger" iconLeft="ban" data-testid="ban-confirm" onClick={ban}>
      Ban and rotate key
    </Button>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-subtle)' }}>Moderation</span>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {iAmCreator && !p.admin && !p.banned && (
          <Button size="sm" variant="secondary" iconLeft="shield-check" onClick={() => app.publish(code, { t: 'role', b: { target: p.pub, admin: true } })}>
            Make admin
          </Button>
        )}
        {p.admin && (
          <Button size="sm" variant="secondary" onClick={() => app.publish(code, { t: 'role', b: { target: p.pub, admin: false } })}>
            Remove admin
          </Button>
        )}
        {banButton}
        {confirmBan && (
          <span data-testid="ban-warning" style={{ flexBasis: '100%', fontSize: 12.5, color: 'var(--text-subtle)' }}>
            They keep what they’ve already read. Everyone else moves to a new key.
          </span>
        )}
      </div>
    </div>
  );
}

function Profile({ id, code }: { id: string; code: string }) {
  const { state, peer, identity } = useCurrent();
  const p = personFor(state, peer, id, identity.pub);
  const canModerate = p.kind === 'human' && !p.self && !!state?.admins.has(identity.pub) && !p.creator;
  return (
    <div style={{ overflow: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ProfileHeader p={p} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <ProfileActions p={p} code={code} me={identity.pub} />
      </div>
      {canModerate && <Moderation p={p} code={code} />}
    </div>
  );
}

function Thread({ id, code, ch }: { id: string; code: string; ch: string }) {
  const { state, peer, identity } = useCurrent();
  const [, setN] = useState(0);
  // useCurrent re-renders on every tick, so the roster is always current.
  const people = roster(state, peer, identity.pub);
  const typing = useTyping(ch);
  const parent = state?.msgs.get(id);
  if (!state || !parent) return <div style={{ padding: 20, fontSize: 14, color: 'var(--text-muted)' }}>This thread shows up once it arrives from the workspace’s relays.</div>;
  const replies = parent.replies.map((rid) => state.msgs.get(rid)).filter((m): m is Msg => !!m);
  const ctx: MsgCtx = {
    state,
    peer,
    me: identity.pub,
    handle: identity.handle.toLowerCase(),
    roster: people,
    code,
    inThread: true,
    forceRender: () => setN((x) => x + 1),
  };
  return (
    <>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '8px 0 12px' }}>
        <MessageItem m={parent} continued={false} ctx={ctx} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-subtle)' }}>
            {parent.replies.length} {parent.replies.length === 1 ? 'reply' : 'replies'}
          </span>
          <span style={{ flex: 1, height: 1, background: 'var(--border-subtle)' }} />
        </div>
        {replies.map((m, i) => {
          const prev = replies[i - 1];
          return <MessageItem key={m.id} m={m} ctx={ctx} continued={!!prev && authorKey(prev) === authorKey(m) && m.ts - prev.ts < 300000 && !m.trace} />;
        })}
      </div>
      <div style={{ padding: '0 12px 12px' }}>
        {typing.length > 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-subtle)', padding: '0 4px 6px' }}>
            {typing.map((t) => t.name).join(', ')} {typing.length === 1 ? 'is' : 'are'} typing…
          </div>
        )}
        <Composer
          key={code + '/' + ch + '/' + id}
          members={people.filter((m) => !m.self)}
          placeholder="Reply in thread"
          onSend={(t, f) => useApp.getState().send(t, f, id)}
          onTyping={() => useApp.getState().setTyping(ch)}
          autoFocus
        />
      </div>
    </>
  );
}

function ResultRow({ m, onClick }: { m: Msg; onClick: () => void }) {
  const { state, peer, identity } = useCurrent();
  const a = personFor(state, peer, authorKey(m), identity.pub);
  const [h, setH] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerEnter={() => setH(true)}
      onPointerLeave={() => setH(false)}
      style={{
        display: 'flex',
        gap: 10,
        textAlign: 'left',
        padding: '10px 12px',
        border: '1px solid var(--border-subtle)',
        borderRadius: 14,
        background: h ? 'var(--surface-hover)' : 'var(--surface-card)',
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <Avatar name={a.name} kind={a.kind} size={24} decorative />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <span style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
          <b style={{ color: 'var(--text-strong)' }}>{a.name}</b> · {m.ch.includes(':') ? channelTitle(state, m.ch, identity.pub) : '#' + channelTitle(state, m.ch, identity.pub)} ·{' '}
          {fmtDay(m.ts)} {fmtTime(m.ts)}
        </span>
        <span style={{ fontSize: 13.5, color: 'var(--text-body)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {m.text || m.files.map((f) => f.name).join(', ')}
        </span>
      </span>
    </button>
  );
}

function openMsg(code: string, m: Msg) {
  const app = useApp.getState();
  if (m.parent) app.go({ code, ch: m.ch, thread: m.parent });
  else {
    app.go({ code, ch: m.ch });
    setTimeout(() => useApp.setState({ highlight: m.id }), 50);
  }
}

function Pinned({ code }: { code: string }) {
  const { state, route } = useCurrent();
  const ids = [...(state?.pins.get(route.ch || '') || [])];
  const msgs = ids
    .map((id) => state?.msgs.get(id))
    .filter((m): m is Msg => !!m && !m.deleted)
    .sort((a, b) => b.ts - a.ts);
  return (
    <div style={{ overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!msgs.length && (
        <div style={{ padding: 8, fontSize: 14, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="pin" size={16} />
          Hover a message and pin it to keep it here.
        </div>
      )}
      {msgs.map((m) => (
        <ResultRow key={m.id} m={m} onClick={() => openMsg(code, m)} />
      ))}
    </div>
  );
}

function Search({ code }: { code: string }) {
  const { state } = useCurrent();
  const [q, setQ] = useState('');
  // Everything in state is a conversation this device may read: others' DMs never arrive here.
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!state || s.length < 2) return [];
    const out: Msg[] = [];
    for (const m of state.msgs.values()) {
      if (m.deleted) continue;
      if (m.text.toLowerCase().includes(s) || m.files.some((f) => f.name.toLowerCase().includes(s))) out.push(m);
    }
    return out.sort((a, b) => b.ts - a.ts).slice(0, 100);
  }, [q, state]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div style={{ padding: 12 }}>
        <Input
          autoFocus
          iconLeft="search"
          placeholder="Search this workspace"
          aria-label="Search this workspace"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          suffix={q ? <span style={{ font: '400 11px var(--font-mono)', color: 'var(--text-subtle)' }}>{results.length}</span> : <Kbd keys="mod+f" size="sm" />}
        />
      </div>
      <div style={{ overflow: 'auto', padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {q.trim().length >= 2 && !results.length && (
          <div style={{ fontSize: 14, color: 'var(--text-muted)', padding: 8 }}>Nothing on this device matches “{q}”. Search covers history synced here.</div>
        )}
        {results.map((m) => (
          <ResultRow key={m.id} m={m} onClick={() => openMsg(code, m)} />
        ))}
      </div>
    </div>
  );
}
